import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ShoppingBasket,
  BadgeEuro,
  Truck,
  HeartHandshake,
  Sparkles,
  Store,
  Clock,
  MapPin,
  ArrowRight,
} from "lucide-react";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { STORE_HOURS } from "@/lib/store-hours";

const highlights: { icon: typeof ShoppingBasket; emoji: string; title: string; text: string }[] = [
  {
    icon: ShoppingBasket,
    emoji: "🛒",
    title: "Τα πάντα σε ένα μέρος",
    text: "Τρόφιμα & είδη καθαριότητας για το νοικοκυριό και τον επαγγελματία — όλα μαζεμένα σε ένα eshop πολυκατάστημα.",
  },
  {
    icon: BadgeEuro,
    emoji: "💸",
    title: "Τιμές που χαμογελάς",
    text: "Στις πιο ανταγωνιστικές τιμές της αγοράς, για τις ανάγκες ακόμη και των πιο απαιτητικών.",
  },
  {
    icon: Truck,
    emoji: "🚚",
    title: "Εγγυημένες παραδόσεις",
    text: "Αξιοπιστία σε ποιότητα, τιμές και παραδόσεις — γρήγορα και με συνέπεια.",
  },
  {
    icon: HeartHandshake,
    emoji: "🐼",
    title: "Ο πελάτης στο κέντρο",
    text: "Είμαστε διαρκώς δίπλα σου με τεχνογνωσία και πλήρη υποστήριξη, πριν και μετά την αγορά σου.",
  },
];

const hours = STORE_HOURS;

function AboutPage() {
  return (
    <StorefrontLayout hideCartSidebar>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-primary/10 via-primary/5 to-background">
        <div className="pointer-events-none absolute -left-10 top-6 text-7xl opacity-20 animate-bounce">🐼</div>
        <div className="pointer-events-none absolute right-6 bottom-6 text-6xl opacity-20">🧺</div>
        <div className="mx-auto max-w-none px-4 py-16 text-center sm:py-20">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/15 px-4 py-1.5 text-sm font-medium text-primary">
            <Sparkles className="h-4 w-4" /> Γεια σου, είμαστε ο MrPanda!
          </span>
          <h1 className="mt-5 text-4xl font-extrabold tracking-tight sm:text-5xl">
            Καλώς ήρθες στην παρέα μας 🐼
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
            Ασχολούμαστε με το χονδρικό και λιανικό εμπόριο ειδών τροφίμων και καθαριότητας,
            καλύπτοντας μεγάλο φάσμα προμηθειών για το νοικοκυριό και τον επαγγελματία —
            με αξιοπιστία σε ποιότητα, τιμές και εγγυημένες παραδόσεις.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg">
              <Link to="/">Δες τα προϊόντα μας <ArrowRight className="h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/contact">Επικοινώνησε μαζί μας</Link>
            </Button>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-none px-4 py-14">
        {/* Highlights */}
        <div className="grid gap-5 sm:grid-cols-2">
          {highlights.map((h) => (
            <Card key={h.title} className="group transition-transform hover:-translate-y-1 hover:shadow-lg">
              <CardContent className="flex items-start gap-4 p-6">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-2xl transition-transform group-hover:scale-110">
                  {h.emoji}
                </span>
                <div>
                  <h2 className="flex items-center gap-2 text-lg font-bold">
                    <h.icon className="h-4 w-4 text-primary" /> {h.title}
                  </h2>
                  <p className="mt-1.5 text-sm text-muted-foreground">{h.text}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Story */}
        <div className="mt-14 rounded-3xl border border-border bg-muted/40 p-6 sm:p-10">
          <h2 className="text-2xl font-bold">Η φιλοσοφία μας 💛</h2>
          <div className="mt-4 space-y-4 text-muted-foreground">
            <p>
              Για τις ανάγκες και των πιο απαιτητικών, σου δίνουμε τη δυνατότητα να βρίσκεις
              συγκεντρωμένες σε ένα eshop πολυκατάστημα λύσεις σε τομείς τόσο διαφορετικούς,
              όσο και συμπληρωματικούς μεταξύ τους.
            </p>
            <p>
              Το κέντρο του ενδιαφέροντός μας είναι ο πελάτης. Γι' αυτό βρισκόμαστε διαρκώς δίπλα του,
              για να του παρέχουμε τεχνογνωσία και πλήρη υποστήριξη για κάθε προϊόν που τον ενδιαφέρει.
              Μη διστάσεις να επικοινωνήσεις μαζί μας ώστε να κάνεις πιο άνετα την ξενάγησή σου στο eshop μας.
            </p>
            <p>
              Με σεβασμό και κατανόηση στις ανάγκες του καταναλωτή, στοχεύουμε στην εγγυημένη ποιότητα
              προϊόντων και στην άμεση εξυπηρέτησή του — όχι μόνο κατά τη διάρκεια της επίσκεψής του στο
              ηλεκτρονικό μας κατάστημα, αλλά και μετά την ολοκλήρωση των αγορών του.
            </p>
            <p className="font-medium text-foreground">
              Θα μας βρεις online στο <span className="text-primary">www.mrpanda.gr</span> αλλά και στο
              φυσικό μας κατάστημα στη Σταυρούπολη Θεσσαλονίκης.
            </p>
          </div>
        </div>

        {/* Hours & Location */}
        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <Card>
            <CardContent className="p-6">
              <h2 className="flex items-center gap-2 text-xl font-bold">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Clock className="h-5 w-5" />
                </span>
                Ωράριο καταστήματος
              </h2>
              <dl className="mt-4 divide-y divide-border">
                {hours.map((h) => (
                  <div key={h.day} className="flex items-baseline justify-between gap-4 py-2.5">
                    <dt className="font-medium">{h.day}</dt>
                    <dd className="text-right text-sm text-muted-foreground">{h.time}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <h2 className="flex items-center gap-2 text-xl font-bold">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Store className="h-5 w-5" />
                </span>
                Τοποθεσία καταστήματος
              </h2>
              <div className="mt-4 flex items-start gap-3 text-muted-foreground">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <p>Π. Λεβαντή 15Α, 564 29 Σταυρούπολη Θεσσαλονίκης, Ελλάδα</p>
              </div>
              <Button asChild variant="outline" className="mt-5">
                <a
                  href="https://www.google.com/maps/place//data=!4m2!3m1!1s0x14a8390054f97e2b:0xbcefb065218ddc4?sa=X&ved=1t:8290&ictx=111"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Άνοιξε στον χάρτη <ArrowRight className="h-4 w-4" />
                </a>
              </Button>

              <div className="mt-5 overflow-hidden rounded-2xl border border-border">
                <iframe
                  title="Χάρτης καταστήματος MrPanda"
                  src="https://maps.google.com/maps?q=40.6635625,22.9400625&z=17&output=embed"
                  className="h-64 w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </StorefrontLayout>
  );
}

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://mrpanda.gr/about" },
      { title: "Σχετικά με εμάς — MrPanda.gr" },
      {
        name: "description",
        content:
          "Ο MrPanda ασχολείται με το χονδρικό και λιανικό εμπόριο ειδών τροφίμων και καθαριότητας, με αξιοπιστία σε ποιότητα, τιμές και εγγυημένες παραδόσεις.",
      },
      { property: "og:title", content: "Σχετικά με εμάς — MrPanda.gr" },
      {
        property: "og:description",
        content:
          "Ο MrPanda ασχολείται με το χονδρικό και λιανικό εμπόριο ειδών τροφίμων και καθαριότητας, με αξιοπιστία σε ποιότητα, τιμές και εγγυημένες παραδόσεις.",
      },
    ],
    links: [{ rel: "canonical", href: "https://mrpanda.gr/about" }],
  }),
  component: AboutPage,
});
